import { Box, Card, Checkbox, Divider, Group, Slider, Stack, Text } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatEur, formatNumber, numberLocale, vfiatToEur } from '@/lib/money';

const formatTotal = (vfiat: number, lang: string) => formatEur(vfiatToEur(vfiat), lang, 2, 2);
const formatPerMsg = (vfiat: number, lang: string) => formatEur(vfiatToEur(vfiat), lang, 4, 4);

const feeMessage = 500;
const feeNotification = 300;
const feeSpamCheck = 500;
const feeIntegration = 50;

const discounts = [
  { min: 10000, discount: 0.85 },
  { min: 20000, discount: 0.8 },
  { min: 30000, discount: 0.75 },
  { min: 100000, discount: 0.7 },
];

const MIN_MSGS = 100;
const MAX_MSGS = 100000;

// Logarithmic slider: 0–100 internal range maps to MIN_MSGS..MAX_MSGS exponentially.
const SLIDER_RANGE = 100;
const LOG_SPAN = Math.log(MAX_MSGS / MIN_MSGS);
const sliderToMessages = (s: number) =>
  Math.round(MIN_MSGS * Math.exp((LOG_SPAN * s) / SLIDER_RANGE));
const messagesToSlider = (m: number) => (Math.log(m / MIN_MSGS) / LOG_SPAN) * SLIDER_RANGE;

const MARKS = [100, 1000, 10000, 30000, 100000];

const sliderMarks = (lang: string) => {
  const compact = new Intl.NumberFormat(numberLocale(lang), { notation: 'compact' });
  return MARKS.map((m) => ({ value: messagesToSlider(m), label: compact.format(m) }));
};

export function FeeCalculator() {
  const { t, i18n } = useTranslation();
  const [sliderValue, setSliderValue] = useState(messagesToSlider(1000));
  const [useNotifications, setUseNotifications] = useState(true);
  const [useSpamCheck, setUseSpamCheck] = useState(true);
  const [useIntegration, setUseIntegration] = useState(true);

  const totalMessages = useMemo(() => sliderToMessages(sliderValue), [sliderValue]);

  const { totalFee, feeAfterDiscount, feePerMessage, hasDiscount } = useMemo(() => {
    let fee = feeMessage;
    if (useNotifications) fee += feeNotification;
    if (useSpamCheck) fee += feeSpamCheck;
    if (useIntegration) fee += feeIntegration;

    const subtotal = fee * totalMessages;
    let discount = 1;
    for (const d of discounts) {
      if (totalMessages >= d.min) discount = d.discount;
    }
    const discounted = subtotal * discount;
    return {
      totalFee: subtotal,
      feeAfterDiscount: discounted,
      feePerMessage: totalMessages > 0 ? discounted / totalMessages : 0,
      hasDiscount: discount < 1,
    };
  }, [totalMessages, useNotifications, useSpamCheck, useIntegration]);

  return (
    <Card withBorder radius="md" p="xl">
      <Stack gap="lg">
        <Box>
          <Group justify="space-between" align="flex-end" mb="xs">
            <Text fw={500} size="sm">
              {t('billing.totalMessages')}
            </Text>
            <Text fw={700} size="lg" lh={1}>
              {formatNumber(totalMessages, i18n.language)}
            </Text>
          </Group>
          <Slider
            value={sliderValue}
            onChange={setSliderValue}
            min={0}
            max={SLIDER_RANGE}
            step={0.1}
            marks={sliderMarks(i18n.language)}
            label={null}
            mb="lg"
          />
          <Text size="xs" c="dimmed" mt="lg">
            {t('billing.usageDescription')}
          </Text>
        </Box>

        <Stack gap="xs">
          <Checkbox
            label={t('billing.checkSpam')}
            checked={useSpamCheck}
            onChange={(e) => setUseSpamCheck(e.currentTarget.checked)}
          />
          <Checkbox
            label={t('billing.emailNotifications')}
            checked={useNotifications}
            onChange={(e) => setUseNotifications(e.currentTarget.checked)}
          />
          <Checkbox
            label={t('billing.webhook')}
            checked={useIntegration}
            onChange={(e) => setUseIntegration(e.currentTarget.checked)}
          />
        </Stack>

        <Divider />

        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Stack gap={2}>
            <Text size="sm" c="dimmed" fw={500} tt="uppercase" style={{ letterSpacing: '0.04em' }}>
              {t('billing.cost').replace(':', '').trim()}
            </Text>
            <Group gap="sm" align="baseline">
              {hasDiscount && (
                <Text size="md" c="dimmed" td="line-through">
                  {formatTotal(totalFee, i18n.language)}
                </Text>
              )}
              <Text size="2rem" fw={700} lh={1}>
                {formatTotal(hasDiscount ? feeAfterDiscount : totalFee, i18n.language)}
              </Text>
            </Group>
          </Stack>
          <Text size="sm" c="dimmed">
            {t('billing.pricing.perMessage', {
              price: formatPerMsg(feePerMessage, i18n.language),
            })}
          </Text>
        </Group>
      </Stack>
    </Card>
  );
}
